import { useQuery } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import type {
  PtAppointmentDTO,
  PtAvailabilityExceptionDTO,
  PtAvailabilityTemplateDTO,
  PtMemberSearchResultDTO,
  PtSlotDTO,
  PtTrainerPublicDTO,
} from "@membentry/shared-types";
import * as api from "@membentry/shared-api";

import { ptKeys } from "./keys";
import type { QueryOptions } from "./types";

const isIsoDate = (date: string) => /^\d{4}-\d{2}-\d{2}$/.test(date);

export const usePtTrainersQuery = (
  client: AxiosInstance,
  clubId: number,
  options?: QueryOptions<PtTrainerPublicDTO[]>,
) =>
  useQuery({
    queryKey: ptKeys.trainers(clubId),
    queryFn: () => api.getPtTrainers(client, clubId),
    enabled: !!clubId,
    ...options,
  });

export const usePtSlotsQuery = (
  client: AxiosInstance,
  clubId: number,
  trainerId: number,
  date: string,
  options?: QueryOptions<PtSlotDTO[]>,
) =>
  useQuery({
    queryKey: ptKeys.slots(clubId, trainerId, date),
    queryFn: () => api.getPtSlots(client, clubId, trainerId, date),
    enabled: !!clubId && !!trainerId && isIsoDate(date),
    ...options,
  });

export const usePtTrainerSlotsQuery = (
  client: AxiosInstance,
  clubId: number,
  trainerId: number,
  date: string,
  options?: QueryOptions<PtSlotDTO[]>,
) =>
  useQuery({
    queryKey: ptKeys.trainerSlots(clubId, trainerId, date),
    queryFn: () => api.getPtSlotsForTrainer(client, clubId, trainerId, date),
    enabled: !!clubId && !!trainerId && isIsoDate(date),
    ...options,
  });

export const useMyPtAppointmentsQuery = (
  client: AxiosInstance,
  clubId: number,
  options?: QueryOptions<PtAppointmentDTO[]>,
) =>
  useQuery({
    queryKey: ptKeys.mine(clubId),
    queryFn: () => api.getMyPtAppointments(client, clubId),
    enabled: !!clubId,
    ...options,
  });

export const usePtAvailabilityQuery = (
  client: AxiosInstance,
  clubId: number,
  options?: QueryOptions<PtAvailabilityTemplateDTO[]>,
) =>
  useQuery({
    queryKey: ptKeys.availability(clubId),
    queryFn: () => api.getPtAvailability(client, clubId),
    enabled: !!clubId,
    ...options,
  });

export const usePtAvailabilityExceptionsQuery = (
  client: AxiosInstance,
  clubId: number,
  from?: string,
  to?: string,
  options?: QueryOptions<PtAvailabilityExceptionDTO[]>,
) =>
  useQuery({
    queryKey: ptKeys.exceptions(clubId, from, to),
    queryFn: () => api.getPtAvailabilityExceptions(client, clubId, { from, to }),
    enabled: !!clubId,
    ...options,
  });

export const usePtRequestsQuery = (
  client: AxiosInstance,
  clubId: number,
  options?: QueryOptions<PtAppointmentDTO[]>,
) =>
  useQuery({
    queryKey: ptKeys.requests(clubId),
    queryFn: () => api.getPtRequests(client, clubId),
    enabled: !!clubId,
    ...options,
  });

export const usePtScheduleQuery = (
  client: AxiosInstance,
  clubId: number,
  from?: string,
  to?: string,
  options?: QueryOptions<PtAppointmentDTO[]>,
) =>
  useQuery({
    queryKey: ptKeys.schedule(clubId, from, to),
    queryFn: () => api.getPtSchedule(client, clubId, from, to),
    enabled: !!clubId,
    ...options,
  });

export const useClubMembersSearchQuery = (
  client: AxiosInstance,
  clubId: number,
  query: string,
  options?: QueryOptions<PtMemberSearchResultDTO[]>,
) =>
  useQuery({
    queryKey: ptKeys.members(clubId, query),
    queryFn: () => api.searchClubMembers(client, clubId, query),
    ...options,
    enabled: (options?.enabled ?? true) && !!clubId && query.trim().length >= 2,
  });
